import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ChooseEntityDialogControlled } from "@/components/ChooseEntityDialog";
import { FolderScopeProvider } from "@/components/ChooseEntityDialog/folder-scope-context-provider";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { Entity, EntityType, getRequiredEntityNumericId, useCreateBlueprint } from "@/queries";
import { displayToast } from "@/utils/toast";

interface CreateEvaluationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
  currentFolderId?: number | null;
}

export const CreateEvaluationDialog: React.FC<CreateEvaluationDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
  onLoadingChange,
  currentFolderId: propFolderId,
}) => {
  const authContext = useAuth();
  const userContext = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { workspaceId, folderId: folderIdString } = useParams();
  const folderId = propFolderId ?? (folderIdString ? Number(folderIdString) : null);

  const [isCreating, setIsCreating] = useState(false);

  const { userToken } = authContext!;
  const activeWorkspaceId = userContext.activeWorkspaceId!;

  const createBlueprintMutation = useCreateBlueprint(userToken!);

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingChange?.(isCreating);
  }, [isCreating, onLoadingChange]);

  const handleDatasetSelection = async (entity: Entity) => {
    try {
      setIsCreating(true);
      onOpenChange(false);
      const response = await createBlueprintMutation.mutateAsync({
        dataset_group_id: getRequiredEntityNumericId(entity.id),
        dataset_version_number: entity.version_number,
        name: undefined!,
        folder_id: folderId || undefined,
      });

      if (!response.success) {
        throw new Error(response.error || "Error creating evaluation");
      }

      const evaluationId = response.report_id;

      if (!evaluationId) {
        throw new Error("Invalid response from server");
      }

      displayToast("Evaluation created successfully", ToastType.success);

      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === "folder-entities" &&
          query.queryKey[1] === workspaceId &&
          query.queryKey[2] === (folderId ? Number(folderId) : undefined),
      });
      queryClient.invalidateQueries({
        queryKey: ["folder-entities", workspaceId, null, undefined, undefined, true],
      });

      navigate(`/workspace/${activeWorkspaceId}/evaluate/blueprints/${evaluationId}`);

      onSuccess?.();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error creating evaluation:", error);
      const getErrorMessage = (err: unknown): string => {
        if (typeof err === "string") {
          return err;
        }

        if (err instanceof Error) {
          return err.message;
        }

        return "Error creating evaluation";
      };

      displayToast(getErrorMessage(error), ToastType.error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <FolderScopeProvider value={folderId ?? null}>
      <ChooseEntityDialogControlled
        open={open}
        config={{
          selectLatest: false,
        }}
        onOpenChange={onOpenChange}
        type={EntityType.DATASET}
        onSelect={handleDatasetSelection}
        currentFolderId={folderId ?? null}
      />
    </FolderScopeProvider>
  );
};

export default CreateEvaluationDialog;
